import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { usePathParam, useQueryParam, useViewport } from '@chilecompra/react-kit'
import { Button, Card, Skeleton, Table, TableRow, Toast, Typography } from '@chilecompra/react-kit/components'
import { CERTIFICATE_ABILITY_STATUSES, CERTIFICATE_ABILITY_STATUSES_FOREIGN } from '../../config/settings/constant'
import { useAuthorization } from '../AuthorizationProvider/AuthorizationProvider.hooks'
import {
  onGetSellerEnrollmentCertificatePdfThunk,
  onGetTenderEnrollmentCertificatePdfThunk
} from '../EnrollmentCertificateSection/EnrollmentCertificateSection.actions'
import { AbilityTag, Container, Root, TableCell } from './CertificateFooterSection.styles'

/**
 * The certificate footer section container.
 */
const CertificateFooterSection = () => {
  const dispatch = useDispatch()
  const {
    size: { isTiny, isSmall }
  } = useViewport()
  const numAcquisition = useQueryParam('numAdquisicion')
  const dni = usePathParam('rut')
  const { userType } = useAuthorization()
  const {
    certificate: { completed, failed, isForeign },
    error,
    loading,
    pdfDownload: { error: errorDownload, loading: downloading }
  } = useSelector(state => state.enrollmentCertificate)

  const handleDownload = event => {
    event.preventDefault()

    if (numAcquisition !== null) {
      dispatch(onGetTenderEnrollmentCertificatePdfThunk({ numAcquisition }))
    }

    if (dni !== undefined) {
      dispatch(
        onGetSellerEnrollmentCertificatePdfThunk({
          dni,
          userType: userType === 'Publico' ? 'Comprador' : userType,
          isForeign
        })
      )
    }
  }

  return !error && !loading && !completed ? null : (
    <Root>
      <Container>
        {loading && <Skeleton height="252.67px" variant="rectangular" width="100%" />}

        {!error && !loading && completed && !failed && (
          <Card color="default" padding="32px 48px">
            <Typography align="left" fontWeight="bold" variant="h4">
              Observaciones:
            </Typography>

            {numAcquisition && (
              <>
                <Typography align="left" fontWeight="bold" margin="10px 0 7px" variant="body2">
                  Persona Jurídica constituida fuera de Chile
                </Typography>

                <Typography align="left" variant="body2">
                  En virtud de los principios de libre concurrencia y de igualdad ante las bases, debe considerarse que
                  los proveedores extranjeros cumplen con la habilidad en el Registro de Proveedores, cuando aquélla
                  haya sido exigida como requisito para contratar, a la luz del artículo 16 de la ley N° 19.886.
                </Typography>
              </>
            )}

            {isForeign && (
              <Typography align="left" variant="body1">
                Un Proveedor extranjero es toda persona natural sin domicilio ni residencia en Chile o persona jurídica
                constituida en el extranjero sin domicilio ni residencia en Chile.
              </Typography>
            )}

            <Table accessibility={{ label: 'Observaciones' }} margin="50px 0 0">
              {!isForeign &&
                CERTIFICATE_ABILITY_STATUSES.map((state, index) => {
                  if (isTiny && !isSmall) {
                    return (
                      <TableRow key={`row-${index}`}>
                        <TableCell disableBottomBorder padding="6px 0" width="150px">
                          <AbilityTag color={state.color} enableIcon padding="12px 0 8px" text={state.label} />
                          {state.description}
                        </TableCell>
                      </TableRow>
                    )
                  }

                  return (
                    <TableRow key={`row-${index}`}>
                      <TableCell disableBottomBorder padding="6px 9px 6px 0" verticalAlign="top" width="150px">
                        <AbilityTag color={state.color} enableIcon padding="0" text={state.label} />
                      </TableCell>
                      <TableCell disableBottomBorder padding="6px 0 6px 9px" verticalAlign="top">
                        {state.description}
                      </TableCell>
                    </TableRow>
                  )
                })}

              {isForeign &&
                CERTIFICATE_ABILITY_STATUSES_FOREIGN.map((state, index) => {
                  if (isTiny && !isSmall) {
                    return (
                      <TableRow key={`row-${index}`}>
                        <TableCell disableBottomBorder padding="6px 0" width="150px">
                          <AbilityTag color={state.color} enableIcon padding="12px 0 8px" text={state.label} />
                          {state.description}
                        </TableCell>
                      </TableRow>
                    )
                  }

                  return (
                    <TableRow key={`row-${index}`}>
                      <TableCell disableBottomBorder padding="6px 9px 6px 0" verticalAlign="top" width="150px">
                        <AbilityTag color={state.color} enableIcon padding="0" text={state.label} />
                      </TableCell>
                      <TableCell disableBottomBorder padding="6px 0 6px 9px" verticalAlign="top">
                        {state.description}
                      </TableCell>
                    </TableRow>
                  )
                })}
            </Table>
          </Card>
        )}
      </Container>

      {!error && completed && !failed && (
        <Container isTiny={isTiny} padding="32px 0 24px">
          <Typography align="center" italic variant="h4">
            {loading ? (
              <>
                <Skeleton width="100%" />
                <Skeleton width="100%" />
                <Skeleton width="75%" />
              </>
            ) : (
              'Se emite el presente certificado conforme a lo establecido en la Ley de Compras Públicas y en el artículo 154 del Reglamento de dicha ley, aprobado por el Decreto N° 661 de 2024, del Ministerio de Hacienda.'
            )}
          </Typography>
        </Container>
      )}

      {errorDownload && !downloading && (
        <Container isTiny={isTiny}>
          <Toast
            align="left"
            border
            margin="0 auto 24px"
            textDirection="vertical"
            title="Lo sentimos, estamos presentando problemas con nuestro servidor"
            severity="error"
          >
            En estos momentos tenemos problemas para disponibilizar la información. Por favor, consulta en unos minutos
            más.
          </Toast>
        </Container>
      )}

      {!error && completed && !failed && (
        <Button color="primary" loading={downloading} disabled={downloading} onClick={handleDownload}>
          Descargar certificado en PDF
        </Button>
      )}
    </Root>
  )
}

export default CertificateFooterSection
